<template>
  <div class="musubi-page musubi-page--overview">
    <header class="musubi-page__head">
      <h1>Yorlin — Marketing Operations</h1>
      <p class="text-muted">
        Lead-gen + marketing automation. Phase 1 + 2.1 admin surfaces are live;
        Phase 2.2+ (delivery / queue / template editor / tracking) ships next.
      </p>
    </header>

    <!--
      Getting-started card — visual-only. Steps are a static, hardcoded list;
      none are "done" because we have no status endpoint to confirm real
      completion yet. Only the "now" (first/next) and "locked" (rest) visual
      states are used — never fake a completed step. Progress bar is pinned
      at 0% for the same reason.
    -->
    <section class="musubi-page__getting-started">
      <div class="gs-card">
        <div class="gs-card__head">
          <div class="gs-card__icon"><CygnusIcon name="rocket" /></div>
          <div>
            <h2>Set up Yorlin</h2>
            <p class="text-muted">Work through these steps to launch your first campaign.</p>
          </div>
        </div>
        <div class="gs-progress-track">
          <div class="gs-progress-fill" :style="{ width: onboardingProgressPct + '%' }"></div>
        </div>
        <p class="gs-progress-caption">
          Step completion isn't wired up yet — this tracks at 0% until the setup-status endpoint ships.
        </p>
        <ul class="gs-list">
          <li
            v-for="(step, i) in onboardingSteps"
            :key="step.label"
            :class="['gs-list__item', i === 0 ? 'gs-list__item--now' : 'gs-list__item--locked']"
          >
            <span class="gs-list__num">{{ i + 1 }}</span>
            <div class="gs-list__body">
              <div class="gs-list__title">{{ step.label }}</div>
              <div class="gs-list__detail">{{ step.detail }}</div>
            </div>
          </li>
        </ul>
      </div>

      <div class="gs-nudge">
        <h3>Ready to reach your first prospects?</h3>
        <p>Verify your sending domain and draft an email to launch your first campaign — Yorlin handles the rest.</p>
        <router-link to="/outreach/campaigns" class="gs-nudge__cta">Go to campaigns →</router-link>
      </div>
    </section>

    <!-- KPI tiles — placeholders V1; real numbers wire when send pipeline ships -->
    <section class="musubi-page__kpis">
      <div class="kpi-card" v-for="card in summaryCards" :key="card.label">
        <div class="kpi-card__value">{{ card.value }}</div>
        <div class="kpi-card__label">{{ card.label }}</div>
        <div class="kpi-card__delta">{{ card.delta }}</div>
      </div>
    </section>

    <!-- Quick-link tiles per major capability -->
    <section class="musubi-page__sections">
      <article class="section-tile" v-for="s in sections" :key="s.title">
        <div class="section-tile__head">
          <CygnusIcon :name="s.icon" class="section-tile__icon" />
          <h2>{{ s.title }}</h2>
        </div>
        <p class="text-muted">{{ s.description }}</p>
        <nav class="section-tile__links">
          <router-link
            v-for="link in s.links"
            :key="link.route"
            :to="link.route"
            class="section-tile__link"
          >
            {{ link.label }}
            <span v-if="link.status" class="section-tile__status">{{ link.status }}</span>
          </router-link>
        </nav>
      </article>
    </section>
  </div>
</template>

<script>
/**
 * MarketingOverview — Musubi home dashboard. Mounts at `/` (via
 * DashboardHomeAlterHook) and `/admin`. Pure presentational: a static
 * getting-started checklist + nudge card, KPI tile placeholders, and
 * quick-link tiles grouped by capability area
 * (audience / email / crawler / funnels / analytics).
 *
 * Real KPI numbers + recent-activity feed wire when the relevant phase
 * lands (audience counts at P2.1 ✓ but no aggregate endpoint yet; send
 * volume at P2.3; crawler discoveries at P9; funnel conversions at P5).
 */
export default {
  name: 'MarketingOverview',
  props: {
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      // V1 placeholders — wire to /api/v1/admin/summary when aggregate endpoint exists.
      // `delta` is a placeholder trend sub-line, not real data — keep it a
      // plain em-dash until the summary endpoint can supply an actual value.
      summaryCards: [
        { label: 'Lists',         value: '—', delta: '—' },
        { label: 'Subscribers',   value: '—', delta: '—' },
        { label: 'Sends (30d)',   value: '—', delta: '—' },
        { label: 'Open rate',     value: '—', delta: '—' },
        { label: 'Leads (30d)',   value: '—', delta: '—' },
        { label: 'Warm leads',    value: '—', delta: '—' },
      ],

      // Getting-started checklist — static V1 content. No step is ever
      // rendered "done": we don't have a status endpoint yet to know which
      // of these a given tenant has actually completed, so faking progress
      // would be actively misleading. Wire this to real per-tenant status
      // once that endpoint exists, and drive `onboardingProgressPct` from it.
      onboardingSteps: [
        { label: 'Verify your sending domain', detail: 'Confirm DKIM & SPF so your mail lands in the inbox.' },
        { label: 'Find your first prospects',  detail: 'Discover leads from a crawl source.' },
        { label: 'Draft your first email',     detail: 'Write the message your first campaign will send.' },
        { label: 'Set up a drip flow',         detail: 'Automate follow-ups for prospects who don’t reply.' },
        { label: 'Connect notifications',      detail: 'Get a ping when a prospect replies.' },
      ],
      onboardingProgressPct: 0,

      sections: [
        {
          title: 'Acquire',
          icon: 'broadcast',
          description: 'Find + qualify leads: discovered-lead triage, crawl sources, intent signals, pipeline.',
          links: [
            { label: 'Prospects',        route: '/acquire/prospects' },
            { label: 'Crawl sources',    route: '/acquire/sources' },
            { label: 'Intent signals',   route: '/acquire/signals' },
            { label: 'Pipeline view',    route: '/acquire' },
          ],
        },
        {
          title: 'Outreach',
          icon: 'envelope',
          description: 'Campaigns, drip flows, mailing lists + the audiences that feed them.',
          links: [
            { label: 'Campaigns',    route: '/outreach/campaigns' },
            { label: 'Drip flows',   route: '/outreach/drip-flows' },
            { label: 'Send history', route: '/outreach/messages' },
            { label: 'Mailing lists',route: '/outreach/lists' },
            { label: 'Segments',     route: '/outreach/segments' },
            { label: 'Subscribers',  route: '/outreach/subscribers' },
          ],
        },
        {
          title: 'Capture',
          icon: 'filter',
          description: 'Landing pages + lead-capture forms (+ later QR codes).',
          links: [
            { label: 'Landing pages', route: '/acquire/landing-pages' },
            { label: 'Lead forms',    route: '/acquire/forms' },
            { label: 'QR codes',      route: '/acquire/qr', status: 'soon' },
          ],
        },
        {
          title: 'Analytics',
          icon: 'chart-line',
          description: 'Attribution + (later) event funnels + the client reporting hub.',
          links: [
            { label: 'Attribution',  route: '/analytics/attribution' },
            { label: 'Event funnels',route: '/analytics/funnels',     status: 'soon' },
            { label: 'Client hub',   route: '/analytics/clients',     status: 'soon' },
          ],
        },
        {
          title: 'Integrations',
          icon: 'plug',
          description: 'TC CRM bridge (warm-lead handoff) + Hermes bridge (direct-purchase) + outbound webhooks.',
          links: [
            { label: 'TC CRM bridge',     route: '/admin/crm-bridge',     status: 'P7' },
            { label: 'Hermes bridge',     route: '/admin/hermes-bridge',  status: 'P8' },
            { label: 'Outbound webhooks', route: '/admin/webhooks-out',   status: 'P13' },
          ],
        },
      ],
    };
  },
};
</script>

<style>
.musubi-page {
  padding: var(--m-space-6, 32px);
  color: var(--m-text-primary, #0F172A);
  font-family: var(--m-font-body, system-ui);
}
.musubi-page__head h1 {
  margin: 0 0 4px;
  font-size: 1.6rem;
  letter-spacing: -0.01em;
}
.musubi-page__head .text-muted {
  color: var(--m-text-secondary, #475569);
  margin-bottom: 24px;
}

/* Getting-started: checklist card + nudge card */
.musubi-page__getting-started {
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  gap: 20px;
  align-items: start;
  margin-bottom: 32px;
}
@media (max-width: 860px) {
  .musubi-page__getting-started {
    grid-template-columns: 1fr;
  }
}
.gs-card {
  background: var(--m-bg-card, #FFF);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius-lg, 14px);
  box-shadow: var(--m-shadow-card);
  overflow: hidden;
}
.gs-card__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--m-border, #E2E8F0);
}
.gs-card__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.gs-card__head .text-muted {
  color: var(--m-text-secondary, #475569);
  font-size: 0.8rem;
  margin-top: 2px;
}
.gs-card__icon {
  width: 34px;
  height: 34px;
  border-radius: var(--m-radius, 8px);
  background: var(--m-accent-soft);
  color: var(--m-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex: none;
}
.gs-progress-track {
  height: 6px;
  background: var(--m-accent-soft);
  border-radius: 999px;
  margin: 14px 20px 4px;
  overflow: hidden;
}
.gs-progress-fill {
  height: 100%;
  /* Indigo → teal brand gradient, driven by onboardingProgressPct (0 today). */
  background: linear-gradient(90deg, var(--m-accent-2), var(--m-accent));
  border-radius: 999px;
  transition: width 200ms ease;
}
.gs-progress-caption {
  font-size: 0.72rem;
  color: var(--m-text-tertiary, #94A3B8);
  padding: 4px 20px 8px;
  margin: 0;
}
.gs-list {
  list-style: none;
  margin: 0;
  padding: 6px 0;
}
.gs-list__item {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 12px 20px;
}
.gs-list__item + .gs-list__item {
  border-top: 1px solid var(--m-border, #EAECF3);
}
.gs-list__num {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 800;
  border: 1.5px solid var(--m-border, #E2E8F0);
  color: var(--m-text-tertiary, #94A3B8);
  background: var(--m-bg-card, #FFF);
}
.gs-list__title {
  font-size: 0.87rem;
  font-weight: 600;
}
.gs-list__detail {
  font-size: 0.75rem;
  color: var(--m-text-tertiary, #94A3B8);
  margin-top: 1px;
}
/* "now" = the next actionable step. */
.gs-list__item--now .gs-list__num {
  background: var(--m-accent);
  border-color: var(--m-accent);
  color: var(--m-accent-text, #FFF);
}
/* "locked" = later steps, dimmed until the step ahead of them is real. */
.gs-list__item--locked {
  opacity: 0.55;
}
/*
 * "done" is defined for when real per-tenant completion is wired up —
 * intentionally unused today so no step is ever shown as finished
 * before we can actually confirm it.
 */
.gs-list__item--done .gs-list__num {
  background: var(--m-status-success);
  border-color: var(--m-status-success);
  color: var(--m-accent-text, #FFF);
}
.gs-list__item--done .gs-list__title {
  color: var(--m-text-tertiary, #94A3B8);
  text-decoration: line-through;
  text-decoration-color: var(--m-border-strong, #CBD5E1);
  font-weight: 500;
}

.gs-nudge {
  background: linear-gradient(135deg, var(--m-accent-soft), #F5F3FF);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius-lg, 14px);
  padding: 18px 20px;
}
.gs-nudge h3 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--m-accent-hover);
}
.gs-nudge p {
  font-size: 0.8rem;
  color: var(--m-text-secondary, #475569);
  margin: 6px 0 14px;
  line-height: 1.5;
}
.gs-nudge__cta {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: var(--m-radius, 8px);
  padding: 9px 16px;
  background: var(--m-accent);
  color: var(--m-accent-text, #FFF);
  text-decoration: none;
  transition: background-color 100ms;
}
.gs-nudge__cta:hover {
  background: var(--m-accent-hover);
}

/* KPI strip */
.musubi-page__kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 32px;
}
.kpi-card {
  background: var(--m-bg-card, #FFF);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius, 6px);
  padding: 16px 18px;
  box-shadow: var(--m-shadow-card);
}
.kpi-card__value {
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 1.1;
}
.kpi-card__label {
  margin-top: 6px;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--m-text-secondary, #475569);
}
.kpi-card__delta {
  margin-top: 4px;
  font-size: 0.73rem;
  color: var(--m-text-tertiary, #94A3B8);
}

/* Section tiles */
.musubi-page__sections {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}
.section-tile {
  background: var(--m-bg-card, #FFF);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius-lg, 14px);
  box-shadow: var(--m-shadow-card);
  padding: 20px;
}
.section-tile__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.section-tile__head h2 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}
.section-tile__icon {
  color: var(--m-accent, #6D28D9);
  font-size: 1.1rem;
}
.section-tile .text-muted {
  color: var(--m-text-secondary, #475569);
  font-size: 0.85rem;
  margin-bottom: 14px;
  line-height: 1.5;
}
.section-tile__links {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.section-tile__link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.85rem;
  color: var(--m-text-primary, #0F172A);
  text-decoration: none;
  transition: background-color 100ms, color 100ms;
}
.section-tile__link:hover {
  background: var(--m-accent-soft, #EDE9FE);
  color: var(--m-accent, #6D28D9);
}
.section-tile__status {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--m-text-tertiary, #94A3B8);
  font-weight: 500;
}
</style>
